import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const API = process.env.REACT_APP_BACKEND_URL;

const fieldConfigs = {
  "Video Production": [
    { key: "Project type", type: "select", options: ["Promo video", "Multi-camera show", "Music video", "Event coverage", "Other"] },
    { key: "Ideal shoot date", type: "date" },
    { key: "Location", type: "text", placeholder: "Where is the shoot?" },
  ],
  "Artist Showreels": [
    { key: "Tribute / act type", type: "text", placeholder: "e.g. Elvis tribute, vocalist, duo…" },
    { key: "How many songs?", type: "select", options: ["3", "4", "5", "5+"] },
    { key: "Ideal dates", type: "text", placeholder: "2–3 dates that work for you" },
  ],
  "Business Content & Promo": [
    { key: "Business name", type: "text", placeholder: "Your company" },
    { key: "What do you need?", type: "select", options: ["Promo video", "Photography", "Video + photo package", "Social content"] },
  ],
  "Event Production": [
    { key: "Type of event", type: "text", placeholder: "Festival, dance show, conference…" },
    { key: "Event date", type: "date" },
    { key: "Venue", type: "text", placeholder: "Venue or location" },
  ],
  "Dance & Theatre Shows": [
    { key: "Venue", type: "text", placeholder: "Theatre / venue" },
    { key: "Show date", type: "date" },
    { key: "Number of students at the school", type: "number", placeholder: "e.g. 120" },
  ],
  "Live Stream Booking": [
    { key: "Event date", type: "date" },
    { key: "Venue", type: "text", placeholder: "Venue / location" },
    { key: "Platform", type: "select", options: ["Microsoft Teams", "Zoom", "YouTube", "Webinar", "Not sure yet"] },
  ],
  "Live & Hybrid Production": [
    { key: "Event date", type: "date" },
    { key: "Venue", type: "text", placeholder: "Venue / location" },
    { key: "Platform", type: "select", options: ["Microsoft Teams", "Zoom", "YouTube", "Webinar", "Not sure yet"] },
  ],
  "Studio Hire": [
    { key: "Which studio?", type: "select", options: ["Photo Studio", "Video Studio", "Podcast Studio", "Not sure yet"] },
    { key: "Preferred date", type: "date" },
  ],
  "Trades Promotion": [
    { key: "Your trade", type: "text", placeholder: "Builder, electrician, plumber…" },
    { key: "Project location", type: "text", placeholder: "Town / site location" },
  ],
  "Funeral Live Stream": [
    { key: "Venue address", type: "text", placeholder: "Crematorium / venue address" },
    { key: "Service date", type: "date" },
    { key: "Service length", type: "select", options: ["Up to 45 minutes", "45\u201360 minutes", "Over 1 hour"] },
  ],
  "Estate Agent Media": [
    { key: "Agency name", type: "text", placeholder: "Your agency" },
    { key: "Est. properties to capture per month", type: "number", placeholder: "e.g. 10" },
  ],
  "Artist Development": [
    { key: "Artist / band name", type: "text", placeholder: "Your stage name" },
    { key: "Looking for", type: "select", options: ["Recording", "Music video", "Photo shoot", "Full development package"] },
  ],
  default: [
    { key: "Which service?", type: "select", options: ["Video Production", "Live Streams", "Artist Showreels", "Business Content & Promo", "Event Production", "Dance & Theatre Shows", "Studio Hire", "Something else"] },
  ],
};

const inputCls = "mt-2 w-full border border-black/15 rounded-lg px-4 py-3 bg-white focus:outline-none focus:border-[#E80042] transition-colors duration-200";

const Field = ({ label, testId, ...props }) => (
  <label className="block">
    <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#555555]">{label}</span>
    {props.rows ? (
      <textarea data-testid={testId} className={`${inputCls} resize-none`} {...props} />
    ) : (
      <input data-testid={testId} className={inputCls} {...props} />
    )}
  </label>
);

export const EnquiryModal = ({ open, onClose, context }) => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [extras, setExtras] = useState({});
  const [status, setStatus] = useState("idle");
  const custom = fieldConfigs[context] || fieldConfigs.default;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      const res = await fetch(`${API}/api/enquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, context, extras }),
      });
      if (!res.ok) throw new Error("failed");
      setStatus("sent");
    } catch {
      setStatus("error");
    }
  };

  const close = () => {
    onClose();
    setTimeout(() => {
      setStatus("idle");
      setForm({ name: "", email: "", phone: "", message: "" });
      setExtras({});
    }, 400);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[95] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={close}
          data-testid="enquiry-modal"
        >
          <motion.div
            initial={{ scale: 0.94, y: 30 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.94, y: 30 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="w-full max-w-lg bg-[#FAF6F0] rounded-lg p-8 relative max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={close}
              data-testid="enquiry-close-button"
              className="absolute top-5 right-5 w-10 h-10 rounded-full bg-[#0A0A0A] text-white font-bold hover:bg-[#E80042] hover:rotate-90 transition-[background-color,transform] duration-300"
            >
              ✕
            </button>

            {status === "sent" ? (
              <div className="py-14 text-center" data-testid="enquiry-success">
                <span className="inline-flex w-16 h-16 rounded-full bg-[#E80042] text-white items-center justify-center text-3xl mb-6">✓</span>
                <h3 className="font-display uppercase text-3xl tracking-tight">Enquiry sent!</h3>
                <p className="text-[#555555] mt-3 max-w-sm mx-auto">Thanks — your {context.toLowerCase()} enquiry is in Alex's inbox. We'll get back to you shortly.</p>
                <button onClick={close} data-testid="enquiry-done-button" className="mt-8 bg-[#0A0A0A] text-white font-grotesk font-bold px-8 py-3.5 rounded-full hover:bg-[#E80042] transition-colors duration-300">
                  Done
                </button>
              </div>
            ) : (
              <>
                <p className="text-xs uppercase tracking-[0.3em] font-semibold text-[#E80042]">Enquire about</p>
                <h3 className="font-display uppercase text-3xl sm:text-4xl tracking-tight mt-2 mb-6">{context}</h3>
                <form onSubmit={submit} className="space-y-4">
                  <Field label="Your name" testId="enquiry-name-input" type="text" required value={form.name} onChange={set("name")} placeholder="Jane Smith" />
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Field label="Email" testId="enquiry-email-input" type="email" required value={form.email} onChange={set("email")} placeholder="you@email.com" />
                    <Field label="Mobile" testId="enquiry-phone-input" type="tel" value={form.phone} onChange={set("phone")} placeholder="Your mobile number" />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    {custom.map((f) => (
                      <label key={f.key} className="block">
                        <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#555555]">{f.key}</span>
                        {f.type === "select" ? (
                          <select
                            data-testid={`enquiry-extra-${f.key.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
                            className={inputCls}
                            value={extras[f.key] || ""}
                            onChange={(e) => setExtras((x) => ({ ...x, [f.key]: e.target.value }))}
                          >
                            <option value="">Select…</option>
                            {f.options.map((o) => (
                              <option key={o} value={o}>{o}</option>
                            ))}
                          </select>
                        ) : (
                          <input
                            data-testid={`enquiry-extra-${f.key.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
                            type={f.type}
                            placeholder={f.placeholder}
                            className={inputCls}
                            value={extras[f.key] || ""}
                            onChange={(e) => setExtras((x) => ({ ...x, [f.key]: e.target.value }))}
                          />
                        )}
                      </label>
                    ))}
                  </div>
                  <Field label="Tell us about your project" testId="enquiry-message-input" rows={4} required value={form.message} onChange={set("message")} placeholder="Dates, venue, ideas — anything helps!" />
                  <button
                    type="submit"
                    disabled={status === "sending"}
                    data-testid="enquiry-submit-button"
                    className="w-full bg-[#E80042] text-white font-grotesk font-bold text-lg py-4 rounded-full hover:bg-[#0A0A0A] transition-colors duration-300 active:scale-[0.98] disabled:opacity-60"
                  >
                    {status === "sending" ? "Sending…" : "Send Enquiry →"}
                  </button>
                  {status === "error" && (
                    <p className="text-sm text-[#E80042] text-center" data-testid="enquiry-error">
                      Something went wrong — please try again or call 01277 280 512.
                    </p>
                  )}
                </form>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
